import { type MemberOnboardingMemberPlanChangesProps, type MemberOnboardingRestrictions } from 'utils/web_api.schemas'

import { QLEDentalPlanCard } from '../components/QLEPlanCard/QLEDentalPlanCard'
import { QLEMedicalPlanCard } from '../components/QLEPlanCard/QLEMedicalPlanCard'
import { QLEVisionPlanCard } from '../components/QLEPlanCard/QLEVisionPlanCard'

interface QLEShowCurrentPlanProps {
  planChangeSummary: MemberOnboardingMemberPlanChangesProps;
  hasFamily: boolean;
  restrictions?: MemberOnboardingRestrictions;
}

/**
 * QLEShowCurrentPlan displays the current plan information for QLE events
 * when all conditions are met (no changes to participants, budget, or plans).
 *
 * It displays:
 * - Medical plan details (from next_plan_year.medical_plan, or current_plan_year if off-thatch/medicare)
 * - Dental plan details (from next_plan_year.dental_plan, or current_plan_year if off-thatch)
 * - Vision plan details (from next_plan_year.vision_plan, or current_plan_year if off-thatch)
 *
 * Unlike the comparison view, this shows only the plans without comparing them.
 * When any plan is off-thatch or medicare, it uses the current year's plan instead of next year's.
 *
 * Note: This component only renders the content. Button rendering is handled
 * by the parent component (RenewalShowSummaryPage) to maintain consistent
 * navigation behavior.
 */
export function QLEShowCurrentPlan ({
  planChangeSummary,
  hasFamily,
  restrictions,
}: QLEShowCurrentPlanProps) {
  return (
    <div data-testid='QLEShowCurrentPlan'>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-3'>
          Your current plans
        </h1>
        <p className='body text-gray-500'>
          As a reminder, you're currently covered by:
        </p>
      </div>

      <div className='space-y-6'>
        <QLEMedicalPlanCard
          planChangeSummary={planChangeSummary}
          hasFamily={hasFamily}
        />
        <QLEDentalPlanCard
          planChangeSummary={planChangeSummary}
          hasFamily={hasFamily}
          restrictions={restrictions || {}}
        />
        <QLEVisionPlanCard
          planChangeSummary={planChangeSummary}
          restrictions={restrictions}
        />
      </div>
    </div>
  )
}

export default QLEShowCurrentPlan
